import { useCallback, useEffect, useMemo, useState } from 'react';

import { api, ApiError } from '../api/client';
import './Transformation.css';

interface RunInfo {
  id: string;
  kind: 'serken' | 'regular';
  status: 'queued' | 'running' | 'completed' | 'failed';
  panels_total: number;
  panels_done: number;
  panels_errored: number;
  panels_pushed_to_it: number;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
  triggered_by: string | null;
  error_message: string | null;
  auto_retry_enabled: boolean;
  auto_retry_attempts_used: number;
  auto_retry_attempts_cap: number;
}

interface RunItem {
  run_id: string;
  inventory_panel_id: string;
  panel_email: string;
  status: 'pending' | 'running' | 'ok' | 'error';
  phase: string | null;
  error_code: string | null;
  error_message: string | null;
  resulting_panel_id: string | null;
  organization_name: string | null;
  pushed_to_internal_tools_at: string | null;
  started_at: string | null;
  finished_at: string | null;
}

function formatElapsed(ms: number): string {
  if (ms < 0) return '0s';
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  const remSec = sec % 60;
  if (min < 60) return remSec === 0 ? `${min}m` : `${min}m ${remSec}s`;
  const hr = Math.floor(min / 60);
  const remMin = min % 60;
  return remMin === 0 ? `${hr}h` : `${hr}h ${remMin}m`;
}

interface Toast {
  type: 'success' | 'error' | 'info';
  message: string;
}

interface Props {
  runId: string;
  onBack: () => void;
}

export function TransformationRun({ runId, onBack }: Props): JSX.Element {
  const [run, setRun] = useState<RunInfo | null>(null);
  const [items, setItems] = useState<RunItem[]>([]);
  const [toast, setToast] = useState<Toast | null>(null);
  const [busyRetry, setBusyRetry] = useState(false);
  const [busyItPush, setBusyItPush] = useState(false);
  const [busyAutoRetry, setBusyAutoRetry] = useState(false);

  const showToast = useCallback((type: Toast['type'], message: string) => {
    setToast({ type, message });
    window.setTimeout(() => setToast(null), 4500);
  }, []);

  const load = useCallback(async (): Promise<void> => {
    try {
      const r = await api<{ run: RunInfo; items: RunItem[] }>(
        `/api/transformation/runs/${runId}`,
      );
      setRun(r.run);
      setItems(r.items);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : err instanceof Error ? err.message : 'Failed to load';
      showToast('error', msg);
    }
  }, [runId, showToast]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!run) return;
    if (run.status !== 'queued' && run.status !== 'running') return;
    const t = window.setInterval(() => {
      void load();
    }, 5000);
    return () => window.clearInterval(t);
  }, [run, load]);

  const errors = useMemo(() => items.filter((i) => i.status === 'error'), [items]);
  const oks = useMemo(() => items.filter((i) => i.status === 'ok'), [items]);
  const pending = useMemo(
    () => items.filter((i) => i.status === 'pending' || i.status === 'running'),
    [items],
  );

  function copyEmails(rows: RunItem[]): void {
    if (rows.length === 0) return;
    const text = rows.map((r) => r.panel_email).join('\n');
    void navigator.clipboard.writeText(text);
    showToast('success', `Copied ${rows.length} email${rows.length === 1 ? '' : 's'} to clipboard.`);
  }

  async function retry(): Promise<void> {
    if (errors.length === 0) return;
    setBusyRetry(true);
    try {
      const r = await api<{ retried: number }>(
        `/api/transformation/runs/${runId}/retry-errors`,
        { method: 'POST' },
      );
      showToast('success', `Re-enqueued ${r.retried} errored panel${r.retried === 1 ? '' : 's'}.`);
      await load();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : err instanceof Error ? err.message : 'Retry failed';
      showToast('error', msg);
    } finally {
      setBusyRetry(false);
    }
  }

  async function toggleAutoRetry(): Promise<void> {
    if (!run) return;
    const next = !run.auto_retry_enabled;
    setBusyAutoRetry(true);
    try {
      await api<{ enabled: boolean; attemptsUsed: number; attemptsCap: number }>(
        `/api/transformation/runs/${runId}/auto-retry`,
        { method: 'POST', body: JSON.stringify({ enabled: next }) },
      );
      showToast(
        'success',
        next
          ? `Auto-retry ON — orchestrator will re-run errored panels until clean (cap ${run.auto_retry_attempts_cap})`
          : 'Auto-retry OFF — manual retries only from now on',
      );
      await load();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : err instanceof Error ? err.message : 'Toggle failed';
      showToast('error', msg);
    } finally {
      setBusyAutoRetry(false);
    }
  }

  async function pushAllToInternalTools(): Promise<void> {
    setBusyItPush(true);
    try {
      const r = await api<{ pushed: number; panelIds: string[] }>(
        `/api/transformation/runs/${runId}/internal-tools-push-all-done`,
        { method: 'POST' },
      );
      if (r.pushed === 0) {
        showToast('info', 'No eligible-unpushed panels — already pushed or run not clean.');
      } else {
        showToast('success', `Pushed ${r.pushed} panel${r.pushed === 1 ? '' : 's'} to Internal Tools.`);
      }
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : err instanceof Error ? err.message : 'Push failed';
      showToast('error', msg);
    } finally {
      setBusyItPush(false);
    }
  }

  if (!run) {
    return (
      <div className="trf-root">
        <div className="trf-empty">Loading run…</div>
      </div>
    );
  }

  const fmt = (iso: string | null): string =>
    iso
      ? new Date(iso).toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        })
      : '—';

  return (
    <div className="trf-root">
      <div className="trf-scroll">
        <div className="trf-runheader">
          <button type="button" className="trf-btn trf-btn--ghost" onClick={onBack}>
            ← Back to Transformation
          </button>
          <h1 className="trf-title">
            Run #{run.id.slice(0, 8)} — {run.kind}
          </h1>
          <span className={`trf-status trf-status--${run.status}`}>{run.status}</span>
        </div>

        {toast && <div className={`trf-toast trf-toast--${toast.type}`}>{toast.message}</div>}

        <div className="trf-runmeta">
          <div>
            <strong>Created:</strong>
            {fmt(run.created_at)}
          </div>
          <div>
            <strong>Started:</strong>
            {fmt(run.started_at)}
          </div>
          <div>
            <strong>Finished:</strong>
            {fmt(run.finished_at)}
          </div>
          <div>
            <strong>Duration:</strong>
            {run.started_at && run.finished_at
              ? formatElapsed(
                  new Date(run.finished_at).getTime() - new Date(run.started_at).getTime(),
                )
              : run.started_at
                ? `${formatElapsed(Date.now() - new Date(run.started_at).getTime())} (running)`
                : '—'}
          </div>
          <div>
            <strong>Pushed to IT:</strong>
            {run.panels_pushed_to_it} / {oks.length}
          </div>
          <div>
            <strong>Triggered by:</strong>
            {run.triggered_by ?? '—'}
          </div>
        </div>

        <div className="trf-runsummary">
          <span className="trf-summary-pill trf-summary-pill--ok">{oks.length} ok</span>
          <span className="trf-summary-pill trf-summary-pill--err">{errors.length} error</span>
          <span className="trf-summary-pill trf-summary-pill--neutral">
            {pending.length} pending/running
          </span>
          <span className="trf-summary-pill">{run.panels_total} total</span>
        </div>

        <div className="trf-runactions">
          <button
            type="button"
            className="trf-btn trf-btn--ghost"
            onClick={() => copyEmails(items)}
            disabled={items.length === 0}
          >
            Copy all emails
          </button>
          <button
            type="button"
            className="trf-btn trf-btn--ghost"
            onClick={() => copyEmails(oks)}
            disabled={oks.length === 0}
          >
            Copy successful ({oks.length})
          </button>
          <button
            type="button"
            className="trf-btn trf-btn--ghost"
            onClick={() => copyEmails(errors)}
            disabled={errors.length === 0}
          >
            Copy errored ({errors.length})
          </button>
          <div className="trf-actions-spacer" />
          <button
            type="button"
            className={`trf-btn ${run.auto_retry_enabled ? 'trf-btn--primary' : 'trf-btn--ghost'}`}
            onClick={() => void toggleAutoRetry()}
            disabled={busyAutoRetry}
            title={
              run.auto_retry_enabled
                ? 'Auto-retry is ON. Orchestrator re-runs errored panels each time the run finalizes, until 0 errors OR cap reached. Click to turn OFF.'
                : 'Click to turn ON auto-retry. Orchestrator will re-run errored panels each finalization until clean OR cap reached.'
            }
          >
            {busyAutoRetry
              ? 'Toggling…'
              : `Auto-retry: ${run.auto_retry_enabled ? 'ON' : 'OFF'} (${run.auto_retry_attempts_used}/${run.auto_retry_attempts_cap})`}
          </button>
          <button
            type="button"
            className={`trf-btn ${errors.length > 0 ? 'trf-btn--primary' : 'trf-btn--ghost'}`}
            onClick={() => void retry()}
            disabled={busyRetry || errors.length === 0}
            title="Re-enqueue all errored items in this run. Items go back to 'pending' and workers retry."
          >
            {busyRetry ? 'Retrying…' : `Retry errors (${errors.length})`}
          </button>
          <button
            type="button"
            className="trf-btn trf-btn--secondary"
            onClick={() => void pushAllToInternalTools()}
            disabled={
              busyItPush ||
              run.status !== 'completed' ||
              run.panels_errored !== 0 ||
              oks.length === 0
            }
            title={
              run.status !== 'completed'
                ? 'Available once the run is fully complete'
                : run.panels_errored !== 0
                  ? 'Push only available when the run has zero errors'
                  : 'Send all OK panels to Internal Tools in one batch (limit = capacity + 1, so capacity 19 → IT receives 20). Idempotent: already-pushed panels are skipped.'
            }
          >
            {busyItPush ? 'Pushing…' : `Push all to Internal Tools (${oks.length})`}
          </button>
        </div>

        <section className="trf-card">
          <div className="trf-table-wrap">
            <table className="trf-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th className="trf-col-center">Status</th>
                  <th>Phase</th>
                  <th>Error</th>
                  <th>Started</th>
                  <th>Finished</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="trf-empty">
                      No items in this run.
                    </td>
                  </tr>
                ) : (
                  items.map((it) => (
                    <tr key={it.inventory_panel_id}>
                      <td className="trf-cell-mono">
                        <div>{it.panel_email}</div>
                        {it.organization_name && (
                          <div className="trf-cell-org">{it.organization_name}</div>
                        )}
                      </td>
                      <td className="trf-col-center">
                        <span className={`trf-status trf-status--${it.status}`}>{it.status}</span>
                        {it.pushed_to_internal_tools_at && (
                          <span
                            className="trf-badge--in-it"
                            title={`Pushed to Internal Tools at ${fmt(it.pushed_to_internal_tools_at)}`}
                          >
                            ✓ in IT
                          </span>
                        )}
                      </td>
                      <td>{it.phase ?? '—'}</td>
                      <td className="trf-cell-faint">
                        {it.error_code ? (
                          <>
                            <strong>{it.error_code}</strong>: {it.error_message}
                          </>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td className="trf-cell-faint">{fmt(it.started_at)}</td>
                      <td className="trf-cell-faint">{fmt(it.finished_at)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
}
